import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  MessageCircle, 
  Link2, 
  CheckCircle2, 
  Clock, 
  Send,
  Smartphone,
  ArrowRight,
  Sparkles,
  Sun,
  Bell,
  Bot,
  Info,
  Copy,
  ExternalLink,
  Loader2,
  Unlink
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NurtureOS WhatsApp Business number (display only)
const NURTUREOS_WA_NUMBER = '+1 555 NurtureOS'; // Placeholder - will be the actual number

const WhatsAppConnectPage = () => {
  // Connection states (from API)
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [linkedNumber, setLinkedNumber] = useState('');
  const [mode1Enabled, setMode1Enabled] = useState(false);
  const [mode2Enabled, setMode2Enabled] = useState(false);
  const [briefingTime, setBriefingTime] = useState('06:30');
  const [lastMessageAt, setLastMessageAt] = useState(null);
  
  // Input states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);

  // Fetch current WhatsApp status on mount
  useEffect(() => {
    fetchWAStatus();
  }, []);

  const fetchWAStatus = async () => {
    try {
      const response = await axios.get(`${API}/family/wa-status`);
      setIsConnected(response.data.linked);
      setLinkedNumber(response.data.phone_number || '');
      setMode1Enabled(response.data.mode1_enabled || false);
      setMode2Enabled(response.data.mode2_enabled || false);
      setBriefingTime(response.data.briefing_time || '06:30');
      setLastMessageAt(response.data.last_message_at);
    } catch (error) {
      console.error('Failed to fetch WA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkNumber = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setIsLinking(true);
    try {
      // Format: ensure +62 prefix for Indonesia
      let formattedNumber = phoneNumber.replace(/\D/g, '');
      if (!formattedNumber.startsWith('62')) {
        formattedNumber = '62' + formattedNumber;
      }
      
      const response = await axios.post(`${API}/family/wa-link`, {
        phone_number: '+' + formattedNumber
      });
      
      if (response.data.success) {
        setIsConnected(true);
        setLinkedNumber(response.data.linked_number);
        setMode1Enabled(true);
        toast.success('WhatsApp number linked successfully!');
      }
    } catch (error) {
      toast.error('Failed to link number');
    } finally {
      setIsLinking(false);
    }
  };

  const handleToggleMode1 = async () => {
    setIsToggling(true);
    try {
      const response = await axios.post(`${API}/family/wa-toggle-mode1`);
      setMode1Enabled(response.data.mode1_enabled);
      toast.success(response.data.mode1_enabled ? 'Forward-to-Extract enabled' : 'Forward-to-Extract disabled');
    } catch (error) {
      toast.error('Failed to toggle mode');
    } finally {
      setIsToggling(false);
    }
  };

  // E5 Mode 2: Toggle morning briefing
  const handleToggleMode2 = async (enabled) => {
    setIsSavingPrefs(true);
    try {
      await axios.post(`${API}/family/wa-preferences`, {
        mode2_enabled: enabled
      });
      setMode2Enabled(enabled);
      toast.success(enabled ? 'Morning briefing enabled!' : 'Morning briefing disabled');
    } catch (error) {
      toast.error('Failed to update preference');
    } finally {
      setIsSavingPrefs(false);
    }
  };

  // E5 Mode 2: Save briefing time
  const handleSaveBriefingTime = async () => {
    setIsSavingPrefs(true);
    try {
      await axios.post(`${API}/family/wa-preferences`, {
        briefing_time: briefingTime
      });
      toast.success(`Briefing time set to ${briefingTime}`);
    } catch (error) {
      toast.error('Failed to save briefing time');
    } finally {
      setIsSavingPrefs(false);
    }
  };

  const handleDisconnect = async () => {
    // For now, we don't have a disconnect endpoint - just show a message
    toast.info('To change your linked number, contact support');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Format last message timestamp
  const formatLastMessage = (timestamp) => {
    if (!timestamp) return 'No messages yet';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} mins ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return date.toLocaleDateString();
  };

  // Sample extraction result for demo
  const sampleExtraction = `Found 3 tasks:
• Ava - Sports Day (Dec 15) - Event
• Ava - Bring PE uniform (Dec 15) - Uniform  
• Ava - Submit permission slip (Dec 13) - Permission

Reply YES to save or NO to discard.`;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-nurture-cream-dark rounded-xl animate-pulse" />
        <div className="h-48 bg-nurture-cream-dark rounded-3xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whatsapp-connect-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-green-100 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            WhatsApp Connect
          </h1>
          <p className="text-nurture-muted mt-1">Forward school messages for automatic task extraction</p>
        </div>
        <Badge variant="outline" className="text-nurture-terracotta border-nurture-terracotta">
          <Sparkles className="w-3 h-3 mr-1" />
          Mode 1: Forward-to-Extract
        </Badge>
      </div>

      {/* Connection Status Card */}
      <Card className={isConnected ? 'border-green-300 bg-green-50/50' : 'border-nurture-cream-dark'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Connection Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isConnected ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-nurture-navy">Connected</p>
                    <p className="text-sm text-nurture-muted">{linkedNumber}</p>
                  </div>
                </div>
                <Button variant="outline" onClick={handleDisconnect} size="sm">
                  <Unlink className="w-4 h-4 mr-1" />
                  Change
                </Button>
              </div>
              
              {/* Mode 1 Toggle */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-white border border-nurture-cream-dark">
                <div className="flex items-center gap-3">
                  <Send className="w-5 h-5 text-nurture-sage" />
                  <div>
                    <span className="font-medium">Forward to Extract</span>
                    <p className="text-xs text-nurture-muted">AI extracts tasks from forwarded school messages</p>
                  </div>
                </div>
                <Switch
                  checked={mode1Enabled}
                  onCheckedChange={handleToggleMode1}
                  disabled={isToggling}
                  data-testid="mode1-toggle"
                />
              </div>
              
              {/* Last Message Status */}
              {mode1Enabled && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-nurture-cream-dark/50">
                  <Clock className="w-4 h-4 text-nurture-muted" />
                  <span className="text-sm text-nurture-muted">
                    Last message: {formatLastMessage(lastMessageAt)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-nurture-cream-dark/50">
                <Info className="w-5 h-5 text-nurture-muted flex-shrink-0" />
                <p className="text-sm text-nurture-muted">
                  Link your WhatsApp number to forward school messages for automatic task extraction
                </p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label>WhatsApp Number</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="flex items-center px-3 bg-nurture-cream-dark rounded-l-lg border border-r-0">
                      <span className="text-sm text-nurture-muted">+62</span>
                    </div>
                    <Input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                      placeholder="812 3456 7890"
                      className="rounded-l-none"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleLinkNumber}
                    disabled={isLinking}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="link-number-btn"
                  >
                    {isLinking ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Linking...
                      </>
                    ) : (
                      <>
                        <Link2 className="w-4 h-4 mr-2" />
                        Link Number
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mode 1 Instructions Card */}
      <Card className="border-2 border-nurture-sage/30 bg-nurture-sage/5">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-nurture-sage/20 flex items-center justify-center">
              <Send className="w-4 h-4 text-nurture-sage" />
            </div>
            <Badge className="bg-nurture-sage text-white">Mode 1: Forward to Extract</Badge>
          </div>
          <CardTitle className="text-lg">How to Use</CardTitle>
          <CardDescription>
            Forward any school WhatsApp message to NurtureOS. AI extracts tasks and adds them to your School Tracker.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-xl bg-white border border-nurture-cream-dark">
            <p className="text-sm font-medium text-nurture-navy mb-3">Instructions:</p>
            <ol className="text-sm text-nurture-muted space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-nurture-sage/20 flex items-center justify-center text-xs font-bold text-nurture-sage flex-shrink-0">1</span>
                <span>Save the NurtureOS number as a contact on your phone</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-nurture-sage/20 flex items-center justify-center text-xs font-bold text-nurture-sage flex-shrink-0">2</span>
                <span>When you receive a school message, forward it to NurtureOS</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-nurture-sage/20 flex items-center justify-center text-xs font-bold text-nurture-sage flex-shrink-0">3</span>
                <span>AI will extract tasks and send you a summary</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-nurture-sage/20 flex items-center justify-center text-xs font-bold text-nurture-sage flex-shrink-0">4</span>
                <span>Reply <strong>YES</strong> to save or <strong>NO</strong> to discard</span>
              </li>
            </ol>
          </div>
          
          {/* NurtureOS Number Display */}
          <div className="flex items-center gap-2 p-4 rounded-xl bg-white border border-green-200">
            <MessageCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-nurture-navy">NurtureOS Number:</span>
            <code className="text-sm bg-green-50 px-3 py-1 rounded font-mono">{NURTUREOS_WA_NUMBER}</code>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={() => copyToClipboard(NURTUREOS_WA_NUMBER)}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Example Result */}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => {
              toast.success('Example: AI Extraction Result', {
                description: sampleExtraction,
                duration: 8000
              });
            }}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            See Example Result
          </Button>
        </CardContent>
      </Card>

      {/* E5 Mode 2: Morning Briefing - Now Active! */}
      <Card className="border-2 border-amber-300/50 bg-amber-50/30">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <Sun className="w-4 h-4 text-amber-600" />
            </div>
            <Badge className="bg-amber-500 text-white">Mode 2: Morning Briefing</Badge>
            {mode2Enabled && <Badge className="bg-green-500 text-white">Active</Badge>}
          </div>
          <CardTitle className="text-lg text-nurture-navy">Daily 06:30 Summary</CardTitle>
          <CardDescription>
            Wake up to your daily briefing: tasks, dinner plan, and quick reply commands.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-white border border-amber-200">
            <div className="flex items-center gap-3">
              <Bell className={`w-5 h-5 ${mode2Enabled ? 'text-amber-600' : 'text-nurture-muted'}`} />
              <div>
                <p className="font-medium text-nurture-navy">Morning Briefing</p>
                <p className="text-xs text-nurture-muted">Receive daily summary at {briefingTime}</p>
              </div>
            </div>
            <Switch
              checked={mode2Enabled}
              onCheckedChange={handleToggleMode2}
              disabled={!isConnected || isSavingPrefs}
              data-testid="mode2-toggle"
            />
          </div>

          {/* Briefing Time Selector */}
          {mode2Enabled && (
            <div className="p-4 rounded-xl bg-white border border-amber-200">
              <Label className="text-sm font-medium text-nurture-navy">Briefing Time (WIB)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="time"
                  value={briefingTime}
                  onChange={(e) => setBriefingTime(e.target.value)}
                  className="w-32 rounded-xl"
                  data-testid="briefing-time-input"
                />
                <Button 
                  size="sm" 
                  onClick={handleSaveBriefingTime}
                  disabled={isSavingPrefs}
                  className="rounded-xl bg-amber-500 hover:bg-amber-600"
                >
                  {isSavingPrefs ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
          )}

          {/* Reply Commands Reference */}
          <div className="p-4 rounded-xl bg-white border border-amber-200">
            <p className="text-sm font-medium text-nurture-navy mb-3">Quick Reply Commands:</p>
            <div className="space-y-2 text-sm text-nurture-muted">
              <div className="flex items-center gap-2">
                <code className="bg-amber-50 px-2 py-0.5 rounded text-amber-700">done 1</code>
                <span>Mark task #1 as complete</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-amber-50 px-2 py-0.5 rounded text-amber-700">swap dinner</code>
                <span>Get a new meal suggestion</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-amber-50 px-2 py-0.5 rounded text-amber-700">handoff</code>
                <span>Get today's handoff summary</span>
              </div>
            </div>
          </div>

          {/* Sample Briefing */}
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-xs font-medium text-amber-700 mb-2">Sample briefing:</p>
            <pre className="text-xs text-nurture-muted whitespace-pre-wrap font-mono">{`Good morning! ☀️ Your NurtureOS briefing:

📚 School tasks:
  • Ava - Sports Day (Dec 15)
  • Max - Math homework

🍽️ Tonight: Nasi Goreng

📋 Tomorrow prep:
  • Pack PE kit for Max

Reply: done [1-5] │ swap dinner │ handoff`}</pre>
          </div>
        </CardContent>
      </Card>

      {/* Mode 3: Full Chatbot - Still Coming Soon */}
      <Card className="border-2 border-nurture-muted/20 bg-nurture-cream-dark/30 opacity-70">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-nurture-muted/20 flex items-center justify-center">
              <Bot className="w-4 h-4 text-nurture-muted" />
            </div>
            <Badge variant="outline" className="text-nurture-muted">Future</Badge>
          </div>
          <CardTitle className="text-lg text-nurture-muted">Mode 3: Full Chatbot</CardTitle>
          <CardDescription>
            Natural language chat with NurtureOS. Add tasks, get summaries, manage your family.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" className="w-full" disabled>
            <Bot className="w-4 h-4 mr-2" />
            Coming in Phase 4
          </Button>
        </CardContent>
      </Card>

      {/* Flow Diagram */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">How Forward-to-Extract Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4">
            {[
              { icon: MessageCircle, label: 'School sends message', color: 'bg-blue-100 text-blue-600' },
              { icon: Send, label: 'You forward to NurtureOS', color: 'bg-green-100 text-green-600' },
              { icon: Sparkles, label: 'AI extracts tasks', color: 'bg-purple-100 text-purple-600' },
              { icon: CheckCircle2, label: 'Reply YES to save', color: 'bg-nurture-sage/20 text-nurture-sage' },
            ].map((step, index) => (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 rounded-full ${step.color} flex items-center justify-center mb-2`}>
                    <step.icon className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-medium text-nurture-navy max-w-[120px]">{step.label}</p>
                </div>
                {index < 3 && (
                  <ArrowRight className="w-6 h-6 text-nurture-muted hidden md:block" />
                )}
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppConnectPage;
